import * as React from 'react';
import * as _ from 'lodash';
import { Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Logging,
  Notification,
  NotificationData,
  stringToDate,
  ControlValidation,
  renderEdit,
  ControlState,
  CustomerStatus,
  ControlStyleClass,
  renderDate,
  API,
  ResponseError,
  Customer,
  hashString,
  CustomerSummaryCollection,
  CustomerSummary,
  getUuidFromLocationHeader,
  UserContext,
  UserInfo,
  getCreatePermission,
  PermissionGroup,
  getEditPermission,
  Permission,
  formatDate,
  DateFormatEnum,
  settingContextObjectLookup,
  settingCustomerCustomField,
  SettingsContextObject,
  DisplayData,
  TaxPayer,
  renderSelect,
  SearchAhead,
  Footer,
  VertexLink,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import CustomerAddEditProps from './CustomerAddEditProps.interface';
import '../../customer-info.scss';
import ContactsAddEdit from '../ContactsAddEdit/ContactsAddEdit';
import useTaxpayers from './useTaxpayers';
import { adjustErrorDescription } from '../Common/Common';

const MaxCustomerLength = 40;

const styles: ControlStyleClass = null;

const stateWritable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const CustomerAddEdit = ({
  gatewayEndPoint,
  customer,
  uuid,
}: CustomerAddEditProps): JSX.Element => {
  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);

  // Create a hash of the Parent Customer Code involved Fields
  const createValidationHash = () : string => {
    // eslint-disable-next-line no-use-before-define
    const hash = hashString(`${parentCustomer}-${taxPayerUuid}`);

    return hash;
  };

  // User Context and Permission Sample
  const userContext: UserInfo = React.useContext(UserContext);
  const permissionContactsCreate: boolean = getCreatePermission(userContext, PermissionGroup.Contacts);
  const permissionContactsEdit: boolean = getEditPermission(userContext, PermissionGroup.Contacts);

  // Logging.Info('User Context in Customer', userContext);

  const [showCustomer, setShowCustomer] = React.useState(true);

  const [searchTaxpayerName, setSearchTaxpayerName] = React.useState(null);
  const [taxpayers, searchingTaxpayers, taxpayersNotification] = useTaxpayers(gatewayEndPoint, searchTaxpayerName);

  const [customerCode, setCustomerCode] = React.useState(customer.code);
  const [customerName, setCustomerName] = React.useState(customer.name);
  const [parentCustomer, setParentCustomer] = React.useState(customer.parent ? customer.parent.code : null);
  const [startDate, setStartDate] = React.useState(stringToDate(customer.effectiveDate));
  const [endDate, setEndDate] = React.useState(stringToDate(customer.endDate));
  const [taxPayerUuid, setTaxPayerUuid] = React.useState(customer.taxpayerDefinition ? customer.taxpayerDefinition.uuid : null);
  const [customField1, setCustomField1] = React.useState(customer.customField1);
  const [customField2, setCustomField2] = React.useState(customer.customField2);
  const [customField3, setCustomField3] = React.useState(customer.customField3);
  const [customField4, setCustomField4] = React.useState(customer.customField4);
  const [customField5, setCustomField5] = React.useState(customer.customField5);
  const [customField6, setCustomField6] = React.useState(customer.customField6);
  const [customField7, setCustomField7] = React.useState(customer.customField7);
  const [customField8, setCustomField8] = React.useState(customer.customField8);
  const [customField9, setCustomField9] = React.useState(customer.customField9);
  const [customField10, setCustomField10] = React.useState(customer.customField10);

  // Customer Status
  const status: CustomerStatus = customer.status || CustomerStatus.Pending;

  // Customer is Pending
  const isPending: boolean = status === CustomerStatus.Pending;

  // Create an Initial Validation hash if the Parent Code is already configured and not in Pending State (In Pending Sate Start date can change)
  const [validatedHash, setValidatedHash] = React.useState(parentCustomer && !isPending ? createValidationHash() : null);

  // The Uuid of the Validated Parent Customer. If we have an initial parent, save Uuid so we dont have to do a validate if parent is not changing
  const [validatedParentCustomerUuid, setValidatedParentCustomerUuid] = React.useState(customer.parent ? customer.parent.uuid : null);

  // The Validated Parent Customer - CustomerSummary object
  const [validatedParentCustomer, setValidatedParentCustomer] = React.useState(null);

  const dispatch = useDispatch();

  const [redirect, setRedirect] = React.useState(null);
  const [notification, setNotification] = React.useState(null);

  // Edit Operation
  const isEdit: boolean = (customer.uuid !== null) && (customer.uuid !== undefined);

  // Customer Code and Start Date can only be edited if Customer is in Pending State
  const stateWriteOnce: ControlState = {
    editable: true,
    disabled: isEdit && !isPending,
    readOnly: isEdit && !isPending,
  };

  Logging.Debug('Customer Add / Edit:', customer);

  // Cancel
  const onCancel = (): void => {
    if (isEdit) {
      // Direct back to Customer View
      setRedirect(`/customer/view/${uuid}/1`);
    } else {
      // Direct back to Customers
      setRedirect('/customers');
    }
  };

  // Validate the Customer Values
  const validateCustomerValues = (): boolean => {
    if (!customerCode) {
      return false;
    }

    if (customerCode.length > MaxCustomerLength) {
      return false;
    }

    if (!customerName) {
      return false;
    }

    if (customerName.length > MaxCustomerLength) {
      return false;
    }

    if (!startDate) {
      return false;
    }

    // Customer Parent Configured but not Validated
    if (parentCustomer && !validatedParentCustomerUuid) {
      return false;
    }

    if (startDate && endDate) {
      if (endDate < startDate) {
        return false;
      }
    }

    if (validatedParentCustomer && startDate) {
      if (validatedParentCustomer.startDate) {
        const parentStartDate = stringToDate(validatedParentCustomer.startDate);

        // child.startDate >= parent.startDate
        if (startDate < parentStartDate) {
          return false;
        }
      }

      // child.startDate <= parent.endDate
      if (validatedParentCustomer.endDate) {
        const parentEndDate = stringToDate(validatedParentCustomer.endDate);

        if (startDate > parentEndDate) {
          return false;
        }
      }
    }

    return true;
  };

  // Make the API Call to Accept the Certificate
  const validateParentCustomer = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      let taxpayer: TaxPayer = null;

      // Taxpayer is selected
      if (taxPayerUuid) {
        // Taxpayer still the same
        if (taxPayerUuid === customer.taxpayerDefinition?.uuid) {
          taxpayer = customer.taxpayerDefinition;
        } else {
          // Taxpayer was changed
          taxpayer = taxpayers ? taxpayers.filter((x) => x.uuid === taxPayerUuid)[0] : null;
        }
      }

      // Validate the Parent Customer Code
      const customersValidated: CustomerSummaryCollection = await api.customer.validateParentCustomerV4(
        parentCustomer,
        taxpayer?.companyCode,
        taxpayer?.departmentCode,
        taxpayer?.divisionCode,
        taxpayer?.partitionName,
      );

      Logging.Info('Validated Parent Customer', customersValidated);

      // Only get Customers that are not Children
      const customersValidatedFiltered: CustomerSummary[] = customersValidated.value
        ? customersValidated.value.filter((x: CustomerSummary) => x.parentUuid === null)
        : null;

      // Success - Should only get one Customer in the Summary List
      if (customersValidatedFiltered && (customersValidatedFiltered.length === 1)) {
        const hash: string = createValidationHash();

        // Save the Successfull Hash
        setValidatedHash(hash);

        // Save the Validated Parent Customer Uuid
        setValidatedParentCustomerUuid(customersValidatedFiltered[0].customerUuid);
        setValidatedParentCustomer(customersValidatedFiltered[0]);
      } else if (customersValidated && customersValidated.value && customersValidated.value.length) { // Failure
        // Clear the Successfull Hash
        setValidatedHash(null);

        // Clear the Validated Parent Customer Uuid
        setValidatedParentCustomerUuid(null);
        setValidatedParentCustomer(null);

        const notificationData: NotificationData = {
          type: 'Warning',
          title: 'Parent Customer Validation Warning',
          description: `Customer '${parentCustomer}' already has a parent.  Only a 1 level deep parent-child relationship is allowed.`,
        };

        setNotification(notificationData);
      } else { // Failure
        // Clear the Successfull Hash
        setValidatedHash(null);

        // Clear the Validated Parent Customer Uuid
        setValidatedParentCustomerUuid(null);
        setValidatedParentCustomer(null);

        const notificationData: NotificationData = {
          type: 'Warning',
          title: 'Parent Customer Validation Warning',
          description: `Unable to validate '${parentCustomer}'.  Make sure the Parent Customer Code and Taxpayer information match.`,
        };

        setNotification(notificationData);
      }
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Parent Customer Validation Error',
        description: `Unable to validate Parent Customer '${parentCustomer}'.`,
        error: error as ResponseError,
      };
      setNotification(notificationData);
    }
  };

  // Validate
  const onValidate = (): void => {
    // Validate
    Logging.Debug('Validate');

    dispatch(validateParentCustomer());
  };

  // Get the Updated Customer Values
  const getCustomerValues = (): Customer => {
    const customerUpdated: Customer = _.cloneDeep(customer);

    customerUpdated.code = customerCode;
    customerUpdated.name = customerName;
    customerUpdated.effectiveDate = formatDate(startDate, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' });
    customerUpdated.endDate = formatDate(endDate, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' });
    customerUpdated.customField1 = customField1;
    customerUpdated.customField2 = customField2;
    customerUpdated.customField3 = customField3;
    customerUpdated.customField4 = customField4;
    customerUpdated.customField5 = customField5;
    customerUpdated.customField6 = customField6;
    customerUpdated.customField7 = customField7;
    customerUpdated.customField8 = customField8;
    customerUpdated.customField9 = customField9;
    customerUpdated.customField10 = customField10;

    // Tax Payer
    customerUpdated.taxpayerDefinition = taxPayerUuid
      ? {
        uuid: taxPayerUuid,
      }
      : null;

    // Parent Customer defined
    if (parentCustomer) {
      // Create the Parent Customer
      customerUpdated.parent = {
        code: null,
        uuid: null,
        name: null,
      };

      // Set the Parent information using the Validated Parent Customer
      customerUpdated.parent.uuid = validatedParentCustomerUuid;
    } else {
      customerUpdated.parent = null;
    }

    return customerUpdated;
  };

  // Make the API Call to Add the Customer
  const addCustomer = (customerUpdated: Customer) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Add the Customer
      const headers: Headers = await api.customer.addCustomerV41(customerUpdated);
      const newCustomerUuid: string = getUuidFromLocationHeader(headers);

      onCancel();
    } catch (error) {
      const responseError: ResponseError = adjustErrorDescription(error as ResponseError, customerUpdated.taxpayerDefinition);

      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Customer Save Error',
        description: `Unable to Save Customer '${customerUpdated.name}'.`,
        error: responseError,
        appendMessagesForStatusCodes: [409, 400],
        hideStatusCodes: [409, 400],
      };

      setNotification(notificationData);
    }
  };

  // Make the API Call to Edit the Customer
  const editCustomer = (customerUpdated: Customer) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Edit the Customer
      await api.customer.editCustomerV41(customerUpdated);

      onCancel();
    } catch (error) {
      const responseError: ResponseError = adjustErrorDescription(error as ResponseError, customerUpdated.taxpayerDefinition);

      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Customer Save Error',
        description: [`Unable to Save Customer '${customerUpdated.name}'.`],
        error: responseError,
        appendMessagesForStatusCodes: [400, 409],
        hideStatusCodes: [400, 409],
      };

      setNotification(notificationData);
    }
  };

  // Save
  const onSave = (): void => {
    // Save
    Logging.Info('Save');

    // Get the Updated Customer Values
    const customerUpdated: Customer = getCustomerValues();

    // Edit
    if (isEdit) {
      dispatch(editCustomer(customerUpdated));
    } else { // Add
      dispatch(addCustomer(customerUpdated));
    }
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    setNotification(null);
  };

  // Cancel Conatct Add / Edit
  const onContactAddEditCancel = (): void => {
    setShowCustomer(true);
  };

  const getTaxpayers = (): DisplayData[] => {
    if (taxpayers) {
      // eslint-disable-next-line arrow-body-style
      return taxpayers.map((item: TaxPayer) => {
        return {
          data: item.uuid,
          display: `${item.taxPayerName} / ${item.partitionName}`,
        };
      });
    }

    // Return the Current Selected TaxPayer
    if (customer.taxpayerDefinition) {
      return [{
        data: customer.taxpayerDefinition.uuid,
        display: `${customer.taxpayerDefinition.taxPayerName} / ${customer.taxpayerDefinition.partitionName}`,
      }];
    }

    return [];
  };

  const searchTaxpayers = (searchValue: string) => {
    Logging.Debug('Searching Taxpayers', searchValue);
    setSearchTaxpayerName(searchValue);
  };

  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    // We dont want this to run on the Initial Render.  Otherwise the initials Validated Parent Customer Code if any) may be cleared
    return (): void => {
      // Clear the validated Hash if any of the Parent Customer Code related fields changes
      setValidatedHash(null);

      // Clear the Validated Parent Customer Uuid
      setValidatedParentCustomerUuid(null);
      setValidatedParentCustomer(null);
    };
  }, [parentCustomer, taxPayerUuid]);

  // Button States
  const cancelVisible = true;
  const validateVisible = (parentCustomer !== null) && (parentCustomer !== '');
  const validateEnabled = !validatedParentCustomerUuid || (createValidationHash() !== validatedHash);
  const saveVisible = true;
  const saveEnabled = (!validateVisible || !validateEnabled) && validateCustomerValues();

  const validation: ControlValidation = {
    requiredFields: ['customerCode', 'customerName', 'startDate'],
    minMaxSize: [{ field: 'customerCode', maxSize: MaxCustomerLength }, { field: 'customerName', maxSize: MaxCustomerLength }],
    customMessages: [],
  };

  if (startDate && endDate) {
    if (endDate < startDate) {
      validation.customMessages = [...validation.customMessages,
        { field: 'endDate', message: 'End Date cannot be before Start Date', type: 'Error' }];
    }
  }

  // Custom Message if Parent Customer Code is Validated or Not
  validation.customMessages = parentCustomer
    ? validatedHash
      ? [...validation.customMessages, { field: 'parentCustomer', message: 'Validated Parent Customer Code', type: 'Success' }]
      : [...validation.customMessages, { field: 'parentCustomer', message: 'Unvalidated Parent Customer Code', type: 'Warning' }]
    : validation.customMessages;

  if (validatedParentCustomer && startDate) {
    if (validatedParentCustomer.startDate) {
      const parentStartDate = stringToDate(validatedParentCustomer.startDate);

      // child.startDate >= parent.startDate
      if (startDate < parentStartDate) {
        Logging.Info('Parent Customer Start Date Validation:', startDate, parentStartDate);

        validation.customMessages = [...validation.customMessages,
          // eslint-disable-next-line max-len
          { field: 'startDate', message: `Start Date cannot be before Parent's Start Date (${validatedParentCustomer.startDate})`, type: 'Error' }];
      }
    }

    // child.startDate <= parent.endDate
    if (validatedParentCustomer.endDate) {
      const parentEndDate = stringToDate(validatedParentCustomer.endDate);

      if (startDate > parentEndDate) {
        Logging.Info('Parent Customer End Date Validation:', startDate, parentEndDate);

        validation.customMessages = [...validation.customMessages,
          // eslint-disable-next-line max-len
          { field: 'startDate', message: `Start Date cannot be after Parent's End Date (${validatedParentCustomer.endDate})`, type: 'Error' }];
      }
    }
  }

  // Add / Edit Contact
  const onAddEditContact = (): void => {
    // Add / Edit Contact
    setShowCustomer(false);
  };

  const searchTaxpayerNameAhead: SearchAhead = {
    searching: searchingTaxpayers,
    searchingText: 'Searching Taxpayers ...',
    noResultsText: 'No Taxpayers found',
    onSearch: searchTaxpayers,
    debounceMilliseconds: 750,
  };

  React.useEffect(() => {
    setNotification(taxpayersNotification);
  }, [taxpayersNotification]);

  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    return (): void => {
      Logging.Info('CustomerAddEdit - Unmount');
      setShowCustomer(false);
    };
  }, []);

  // Redirect If Needed
  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  const customFieldHeader1: string = settingContextObjectLookup(settingsContext.customers, 1, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader2: string = settingContextObjectLookup(settingsContext.customers, 2, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader3: string = settingContextObjectLookup(settingsContext.customers, 3, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader4: string = settingContextObjectLookup(settingsContext.customers, 4, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader5: string = settingContextObjectLookup(settingsContext.customers, 5, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader6: string = settingContextObjectLookup(settingsContext.customers, 6, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader7: string = settingContextObjectLookup(settingsContext.customers, 7, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader8: string = settingContextObjectLookup(settingsContext.customers, 8, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader9: string = settingContextObjectLookup(settingsContext.customers, 9, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader10: string = settingContextObjectLookup(settingsContext.customers, 10, settingCustomerCustomField, 'Custom Field');

  const addEditCustomerContact = isEdit ? showCustomer ? 'Edit Customer' : 'Edit Contact(s)' : showCustomer ? 'Add Customer' : 'Add Contact(s)';
  const addEditContact = isEdit ? 'Edit Contacts' : 'Add Contacts';
  const showAddEditContact = showCustomer && (isEdit ? permissionContactsEdit : permissionContactsCreate);
  const enableAddEditContact = saveEnabled;

  return (
    <div className="customerInfoContainer">
      <div className="customerInfoHeader">
        <div className="customerInfoHeaderBackToCustomers">
          <VertexLink href="#/customers" display="Back to Customers" />
        </div>
        <div className="customerInfoHeaderMain">
          <div className="customerInfoHeaderMainLeft">
            <div className="customerHeaderName">
              <span>{addEditCustomerContact}</span>
            </div>
          </div>
          <div className="customerInfoHeaderMainRight">
            {
              showAddEditContact
              && (
                <div className="customerInfoHeaderItemBase">
                  <VtxButton
                    id="addEditContactButton"
                    icon={<VtxIcon type={isEdit ? 'edit' : 'plus'} />}
                    disabled={!enableAddEditContact}
                    onClick={onAddEditContact}
                    type="primary"
                  >
                    {addEditContact}
                  </VtxButton>
                </div>
              )
            }
          </div>
        </div>
      </div>
      <hr />
      {
        showCustomer
        && (
          <>
            <div className="customerInfoContentContainer">
              <div className="customerTabsContainer">
                <div className="generalContainer">
                  {renderEdit(25, 'customerCode', 'Customer Code*', 'text', 'Customer Code',
                    customerCode, setCustomerCode, stateWriteOnce, styles, validation)}
                  {renderEdit(25, 'customerName', 'Customer Name*', 'text', 'Customer Name',
                    customerName, setCustomerName, stateWritable, styles, validation)}
                  {renderEdit(25, 'parentCustomer', 'Parent Customer Code', 'text', 'Parent Customer Code',
                    parentCustomer, setParentCustomer, stateWritable, styles, validation)}
                  {renderSelect(25, 'taxpayerUuid', 'Taxpayer', 'Search by Taxpayer', false, false, getTaxpayers(),
                    true, searchTaxpayerNameAhead, taxPayerUuid, setTaxPayerUuid, stateWritable, styles, validation)}
                  {renderDate(25, 'startDate', 'Start Date*', 'Select Start Date', startDate, setStartDate, null, stateWriteOnce,
                    styles, validation)}
                  {renderDate(25, 'endDate', 'End Date', 'Select End Date', endDate, setEndDate, null, stateWritable,
                    styles, validation)}
                  {
                    customFieldHeader1
                    && (
                      renderEdit(25, 'customField1', customFieldHeader1, 'text', null,
                        customField1, setCustomField1, stateWritable, styles, validation)
                    )
                  }
                  {
                    customFieldHeader2
                    && (
                      renderEdit(25, 'customField2', customFieldHeader2, 'text', null,
                        customField2, setCustomField2, stateWritable, styles, validation)
                    )
                  }
                  {
                    customFieldHeader3
                    && (
                      renderEdit(25, 'customField3', customFieldHeader3, 'text', null,
                        customField3, setCustomField3, stateWritable, styles, validation)
                    )
                  }
                  {
                    customFieldHeader4
                    && (
                      renderEdit(25, 'customField4', customFieldHeader4, 'text', null,
                        customField4, setCustomField4, stateWritable, styles, validation)
                    )
                  }
                  {
                    customFieldHeader5
                    && (
                      renderEdit(25, 'customField5', customFieldHeader5, 'text', null,
                        customField5, setCustomField5, stateWritable, styles, validation)
                    )
                  }
                  {
                    customFieldHeader6
                    && (
                      renderEdit(25, 'customField6', customFieldHeader6, 'text', null,
                        customField6, setCustomField6, stateWritable, styles, validation)
                    )
                  }
                  {
                    customFieldHeader7
                    && (
                      renderEdit(25, 'customField7', customFieldHeader7, 'text', null,
                        customField7, setCustomField7, stateWritable, styles, validation)
                    )
                  }
                  {
                    customFieldHeader8
                    && (
                      renderEdit(25, 'customField8', customFieldHeader8, 'text', null,
                        customField8, setCustomField8, stateWritable, styles, validation)
                    )
                  }
                  {
                    customFieldHeader9
                    && (
                      renderEdit(25, 'customField9', customFieldHeader9, 'text', null,
                        customField9, setCustomField9, stateWritable, styles, validation)
                    )
                  }
                  {
                    customFieldHeader10
                    && (
                      renderEdit(25, 'customField10', customFieldHeader10, 'text', null,
                        customField10, setCustomField10, stateWritable, styles, validation)
                    )
                  }
                </div>
              </div>
            </div>
            <Footer
              onCancel={onCancel}
              cancelVisible={cancelVisible}
              validateVisible={validateVisible}
              validateEnabled={validateEnabled}
              onValidate={onValidate}
              validateText="Validate Parent"
              saveVisible={saveVisible}
              saveEnabled={saveEnabled}
              onSave={onSave}
            />
            <Notification
              data={notification}
              onNotificationClose={onNotificationClose}
              autoClose
            />
          </>
        )
      }
      {
        !showCustomer
        && (
          <ContactsAddEdit
            gatewayEndPoint={gatewayEndPoint}
            customer={getCustomerValues()}
            onContactAddEditCancel={onContactAddEditCancel}
            isEdit={isEdit}
          />
        )
      }
    </div>
  );
};

export default CustomerAddEdit;
